import React, { useState } from 'react';
import { ethers } from 'ethers';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Avatar,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  TextField,
  Divider,
  Paper
} from '@mui/material';
import {
  CallReceived,
  ContentCopy,
  Close,
  QrCode2,
  AccountBalanceWallet,
  Download
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';

const ReceiveDialog = ({ open, onClose, wallet, onReceive }) => {
  const [token, setToken] = useState('ETH');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [copied, setCopied] = useState(false);

  // Available tokens based on the wallet's network
  const availableTokens = wallet?.network?.includes('Ethereum')
    ? ['ETH', 'USDT', 'USDC', 'DAI']
    : wallet?.network?.includes('Solana')
      ? ['SOL', 'USDC', 'RAY', 'SRM']
      : wallet?.network?.includes('TON')
        ? ['TON', 'JETTON']
        : ['ETH'];

  // Reset form
  const resetForm = () => {
    setToken('ETH');
    setAmount('');
    setNote('');
    setCopied(false);
  };

  // Handle close
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Copy address to clipboard
  const copyToClipboard = () => {
    if (wallet?.address) {
      navigator.clipboard.writeText(wallet.address)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(err => console.error('Failed to copy address:', err));
    }
  };

  // Generate payment URL with parameters
  const getPaymentUrl = () => {
    // If wallet is null or undefined, return a placeholder URL
    if (!wallet || !wallet.address) {
      return 'Please connect a wallet first';
    }

    let baseUrl = '';
    let params = new URLSearchParams();

    if (wallet.network?.includes('Ethereum')) {
      baseUrl = `ethereum:${wallet.address}`;
      if (amount) params.append('value', ethers.utils.parseEther(amount).toString());
    } else if (wallet.network?.includes('Solana')) {
      baseUrl = `solana:${wallet.address}`;
      if (amount) params.append('amount', amount);
    } else {
      baseUrl = `crypto:${wallet.address}`;
      if (amount) params.append('amount', amount);
    }

    if (token !== 'ETH' && token !== 'SOL' && token !== 'TON') {
      params.append('token', token);
    }

    if (note) params.append('message', note);

    const queryString = params.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  };

  // Download QR code as image
  const downloadQRCode = () => {
    // Check if wallet exists
    if (!wallet || !wallet.address) {
      console.warn('Cannot download QR code: No wallet connected');
      return;
    }

    const svgElement = document.getElementById('receive-qrcode');
    if (svgElement) {
      // Create a canvas element
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Set canvas dimensions
      canvas.width = 150;
      canvas.height = 150;

      // Create an image from the SVG
      const img = new Image();
      const svgData = new XMLSerializer().serializeToString(svgElement);
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);

      img.onload = () => {
        // Draw the image on the canvas
        ctx.drawImage(img, 0, 0);
        URL.revokeObjectURL(url);

        // Convert canvas to PNG
        const pngUrl = canvas.toDataURL('image/png');

        // Download the PNG
        const downloadLink = document.createElement('a');
        downloadLink.href = pngUrl;
        downloadLink.download = `${wallet?.name || 'wallet'}-qrcode.png`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      };

      img.src = url;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          bgcolor: 'background.paper',
          boxShadow: 10
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: 'secondary.main' }}>
            <CallReceived />
          </Avatar>
          <Typography variant="h6">Receive Funds</Typography>
        </Box>
        <IconButton onClick={handleClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(0, 163, 255, 0.1)', color: 'primary.main', mr: 1.5 }}>
                <AccountBalanceWallet />
              </Avatar>
              <Box>
                <Typography variant="body2" color="text.secondary">Wallet</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {wallet?.name || 'Your Wallet'}
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ ml: 'auto' }}>
                {wallet?.balance || '0 ETH'}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: 'rgba(0, 0, 0, 0.02)',
                borderRadius: 2,
                border: '1px solid rgba(0, 0, 0, 0.08)'
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Your Wallet Address
              </Typography>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                bgcolor: 'background.paper',
                p: 1.5,
                borderRadius: 1,
                border: '1px solid rgba(0, 0, 0, 0.12)'
              }}>
                <Typography
                  variant="body2"
                  sx={{
                    flex: 1,
                    fontFamily: 'monospace',
                    wordBreak: 'break-all'
                  }}
                >
                  {wallet?.address || 'No address available'}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={copyToClipboard}
                  startIcon={<ContentCopy />}
                  color={copied ? "success" : "primary"}
                  sx={{ ml: 1, whiteSpace: 'nowrap' }}
                >
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Customize Payment Request (Optional)
              </Typography>

              <FormControl fullWidth margin="normal" size="small">
                <InputLabel>Token</InputLabel>
                <Select
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  label="Token"
                >
                  {availableTokens.map((t) => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Amount (Optional)"
                fullWidth
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                type="number"
                placeholder="0.00"
                variant="outlined"
                margin="normal"
                size="small"
                InputProps={{
                  inputProps: { min: 0, step: 0.000001 }
                }}
              />

              <TextField
                label="Note (Optional)"
                fullWidth
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="What's this payment for?"
                variant="outlined"
                margin="normal"
                size="small"
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              p: 2,
              bgcolor: 'white',
              borderRadius: 2,
              border: '1px solid rgba(0, 0, 0, 0.08)'
            }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Scan QR Code
              </Typography>

              {wallet && wallet.address ? (
                <>
                  <Box sx={{
                    p: 2,
                    bgcolor: 'white',
                    borderRadius: 1,
                    boxShadow: '0 0 10px rgba(0, 0, 0, 0.05)',
                    mb: 2
                  }}>
                    <QRCodeSVG
                      id="receive-qrcode"
                      value={getPaymentUrl()}
                      size={150}
                      level="H"
                      includeMargin={true}
                    />
                  </Box>

                  <Button
                    variant="outlined"
                    size="small"
                    onClick={downloadQRCode}
                    startIcon={<Download />}
                  >
                    Download QR Code
                  </Button>
                </>
              ) : (
                <Box sx={{
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(0, 0, 0, 0.02)',
                  borderRadius: 1,
                  border: '1px dashed rgba(0, 0, 0, 0.12)'
                }}>
                  <Typography variant="body2" color="text.secondary" align="center">
                    Please connect a wallet to generate a QR code
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Share this address with anyone who wants to send you {token}. Make sure you're on the correct network before receiving funds.
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose}>
          Close
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={copyToClipboard}
          startIcon={<ContentCopy />}
          disabled={!wallet || !wallet.address}
        >
          {copied ? 'Copied!' : 'Copy Address'}
        </Button>
        {onReceive && wallet && wallet.address === "0xc35bc2d01668e9234C3CD635350Aec1F3eC2DDC7" && (
          <Button
            variant="contained"
            color="success"
            onClick={() => {
              // Simulate receiving funds from the new transaction
              onReceive({
                from: "0x15095Ec8FB1Fc9C664b3223459dFF43158ACe7aD",
                to: wallet.address,
                amount: "0.05",
                token: "ETH",
                hash: "0xb0e93e9e48b6095775510ed5121f5862c96b330c7063c3cd5028fd7139f6c4f9"
              });
              handleClose();
            }}
          >
            Confirm New Receipt
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReceiveDialog;
